<template>
  <div>
    <div class="d-flex align-center justify-space-between flex-wrap mb-4">
      <div class="d-flex align-center flex-wrap">
        <v-btn
          v-if="isManager"
          style="margin-top: -2.25rem"
          @click="openUpsertDialog()"
          :disabled="!arenaId"
          color="primary"
          class="mb-8 mr-2">
          {{ $t('entities.products.createNewArenaProduct') }}
        </v-btn>
        <v-btn
          v-if="isManager"
          style="margin-top: -2.25rem"
          @click="navigateToGlobalProducts"
          :disabled="!arenaId"
          color="primary"
          outlined
          class="mb-8">
          {{ $t('entities.products.addProductFromGlobalCatalog') }}
        </v-btn>
      </div>
      <CatalogSwitch
        v-if="isAdmin || isManager"
        :route="{ name: 'entities.products.globalProducts' }"
        :initial-value="$t('entities.products.titleV2')"
        :options="[
          $t('entities.products.titleV2'),
          $t('entities.products.globalProducts'),
        ]" />
    </div>
    <v-card class="elevation-2">
      <v-row class="px-4 mb-6" align="center" justify="start">
        <v-col cols="12" sm="6" md="2" class="text-center">
          <v-select
            v-model="pagination.per_page"
            :items="rowsPerPageItems"
            :menu-props="{ bottom: true, offsetY: true }"
            :no-data-text="$t('components.noData.noEntriesFound')"
            :disabled="!rowsPerPageItems.length || isLoadingProducts"
            hide-details="auto"
            solo>
            <template v-slot:label>
              {{ $t('components.rowsPerPage.entries') }}
            </template>
            <template v-slot:selection="data">
              {{ $t('components.rowsPerPage.entries') }}: {{ data.item }}
            </template>
          </v-select>
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <v-select
            v-model="filters.sort"
            :items="sortOptions"
            :disabled="isLoadingProducts || !arenaId"
            :label="$t('miscellaneous.sortBy')"
            solo
            hide-details="auto" />
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <v-select
            v-model="filters.has_active_variant"
            :items="variantStatusOptions"
            :disabled="isLoadingProducts || !arenaId"
            :label="$t('entities.products.filters.activeVariants')"
            solo
            hide-details="auto" />
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <v-select
            v-model="filters.category_code"
            :items="categoryOptions"
            :disabled="isLoadingProducts || !arenaId"
            :label="$t('entities.categories.entity')"
            clearable
            solo
            hide-details="auto" />
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-text-field
            v-model="nameFilter"
            :placeholder="searchFieldPlaceholder"
            :disabled="isLoadingProducts || !arenaId"
            prepend-inner-icon="mdi-magnify"
            solo
            clearable
            hide-details />
        </v-col>
      </v-row>
      <CardList
        v-if="arenaId && products.length"
        @product:upsert="openUpsertDialog"
        :categories="categories"
        :products="products" />
      <p v-else-if="arenaId" class="text-center pb-8">
        {{
          isLoadingProducts
            ? $t('components.noData.fetchingEntries')
            : $t('components.noData.noEntriesFound')
        }}
      </p>
      <p v-else>Please select an arena to load products.</p>
      <DataTableFooter :loading="isLoadingProducts" :pagination="pagination" />
    </v-card>

    <UpsertDialog
      v-model="isUpsertDialogOpen"
      @product:upsert="upsertArenaProduct"
      @product:delete="deleteArenaProduct"
      :arena-id="arenaId"
      :categories="categories"
      :delete-loading="isDeleteLoading"
      :is-submitting="isUpsertLoading"
      :product="productToUpsert"
      :taxes="taxes" />
  </div>
</template>

<script>
import ArenaProduct from '@/models/shop/ArenaProduct'
import CategoriesApi from '@/services/shop/CategoriesApiV2'
import CardList from './CardList.vue'
import CatalogSwitch from '@/components/CatalogSwitch.vue'
import DataTableFooter from '@/components/crud/DataTableFooter.vue'
import isRole from '@/mixins/crud/computed/isRole'
import TaxJurisdiction from '@/models/setup/TaxJurisdiction'
import UpsertDialog from './UpsertDialog.vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'

const resetPagination = () => ({
  search: '',
  current_page: 1,
  from: 1,
  last_page: 0,
  path: '',
  per_page: 9,
  to: 0,
  total: 0,
})

const resetFilters = () => ({
  sort: '-created_at',
  has_active_variant: null,
  category_code: null,
})

export default {
  name: 'ArenaProducts',
  components: {
    CardList,
    CatalogSwitch,
    DataTableFooter,
    UpsertDialog,
  },
  mixins: [isRole],
  data() {
    return {
      categories: [],
      isDeleteLoading: false,
      isLoadingProducts: false,
      isLoadingTaxJurisdiction: false,
      isUpsertDialogOpen: false,
      isUpsertLoading: false,
      options: {}, // paginationWatch mixin is dependent on this, but it is not used  for anything. It just has to exist.
      pagination: resetPagination(),
      products: [],
      filters: resetFilters(),
      productToUpsert: null,
      isResettingFilters: false,
      selectedSearchAttributes: [],
      taxJurisdiction: null,
      typing: false,
      nameFilter: '',
      nameDebounce: null,
    }
  },
  computed: {
    ...mapGetters(['selectedGlobalProduct']),
    arenaId() {
      return this.$store.getters.arena?.id
    },
    categoryOptions() {
      const categoryItems = Array.isArray(this.categories)
        ? this.categories
            .filter((category) => !!category?.code)
            .map((category) => ({
              text: category?.name || category.code,
              value: category.code,
            }))
        : []
      return [{ text: 'All categories', value: null }, ...categoryItems]
    },
    sortOptions() {
      return [
        {
          text: this.$t('entities.products.filters.newest'),
          value: '-created_at',
        },
        {
          text: this.$t('entities.products.filters.oldest'),
          value: 'created_at',
        },
        {
          text: this.$t('entities.products.filters.nameAsc'),
          value: 'name',
        },
        {
          text: this.$t('entities.products.filters.nameDesc'),
          value: '-name',
        },
        {
          text: this.$t('entities.products.filters.categoryAsc'),
          value: 'category',
        },
        {
          text: this.$t('entities.products.filters.categoryDesc'),
          value: '-category',
        },
      ]
    },
    taxes() {
      return this.taxJurisdiction?.taxes || []
    },
    rowsPerPageItems() {
      return [6, 9, 12, 24, 36]
    },
    variantStatusOptions() {
      return [
        {
          text: this.$t('entities.products.filters.anyStatus'),
          value: null,
        },
        {
          text: this.$t('entities.products.filters.hasActive'),
          value: true,
        },
        {
          text: this.$t('entities.products.filters.hasInactive'),
          value: false,
        },
      ]
    },
    searchFieldPlaceholder() {
      const searchBy = this.$t('components.dataTableHeader.searchBy')
      const name = this.$t('generalAttributes.name')
      return `${searchBy} ${name}`
    },
  },
  methods: {
    ...mapActions(['showSnackbar']),
    ...mapMutations(['setSelectedGlobalProduct']),
    async fetchCategories() {
      try {
        const response = await CategoriesApi.get()
        this.categories = response.data?.data || []
      } catch (error) {
        console.error(error)
        this.categories = []
      }
    },
    async fetchEntities(params = this.setParams()) {
      if (!this.arenaId) {
        this.products = []
        this.setPaginationTotals()
        return
      }
      this.isLoadingProducts = true
      try {
        const response = await ArenaProduct.api.list(this.arenaId, params)
        const data = response.data
        const payload = Array.isArray(data) ? data : data?.data || []
        this.products = Array.isArray(payload) ? payload : []
        this.setPaginationTotals(data.metadata, this.products.length)
      } catch (error) {
        console.error(error)
        this.products = []
        this.setPaginationTotals()
      } finally {
        this.isLoadingProducts = false
      }
    },
    async fetchTaxJurisdiction() {
      if (!this.arenaId) {
        this.taxJurisdiction = null
        return
      }
      this.isLoadingTaxJurisdiction = true
      try {
        const { api } = TaxJurisdiction
        const { data } = await api.getArenaTaxJurisdiction(this.arenaId)
        this.taxJurisdiction = new TaxJurisdiction(data)
      } catch (error) {
        console.error(error)
        this.taxJurisdiction = null
      } finally {
        this.isLoadingTaxJurisdiction = false
      }
    },
    async deleteArenaProduct(productId) {
      if (!this.arenaId || !productId) {
        return
      }
      this.isDeleteLoading = true
      try {
        await ArenaProduct.api.delete(this.arenaId, productId)
        await this.handleProductDeleted()
      } catch (error) {
        console.error(error)
        const errorKey = error?.response?.data?.l10n_message_key
        if (errorKey?.includes('ERR046')) {
          this.showSnackbar({
            color: 'error',
            message: this.$t('entities.products.deleteBlockedByPromotion'),
          })
        }
      } finally {
        this.isDeleteLoading = false
      }
    },
    async handleProductCreated() {
      if (this.arenaId) {
        await this.fetchEntities()
      }
    },
    async handleProductDeleted() {
      if (this.arenaId) {
        await this.fetchEntities()
        this.isUpsertDialogOpen = false
      }
    },
    navigateToGlobalProducts() {
      const arenaId = this.$route.params.arena || this.arenaId
      this.$router.push({
        name: 'entities.products.globalProducts',
        params: { arena: String(arenaId) },
      })
    },
    openUpsertDialog(product = null) {
      this.productToUpsert = product
      this.isUpsertDialogOpen = true
    },
    async upsertArenaProduct(payload) {
      this.isUpsertLoading = true
      const {
        variants = [],
        deletedVariantIds = [],
        ...productPayload
      } = payload || {}
      let productId
      try {
        if (productPayload.id) {
          const hasProductChanges = Object.keys(productPayload).some(
            (key) => key !== 'id',
          )
          if (hasProductChanges) {
            const response = await ArenaProduct.api.update(
              this.arenaId,
              productPayload.id,
              productPayload,
            )
            productId = response.data?.id
            await this.toggleProductTaxes(productPayload)
          } else {
            productId = productPayload.id
          }
        } else {
          const response = await ArenaProduct.api.create(
            this.arenaId,
            productPayload,
          )
          productId = response.data?.id
        }
        if (productId && Array.isArray(variants) && variants.length) {
          await this.createArenaVariants(productId, variants)
        }
        if (productId && deletedVariantIds.length) {
          await this.deleteArenaVariants(productId, deletedVariantIds)
        }
        await this.handleProductCreated()
        this.isUpsertDialogOpen = false
      } catch (error) {
        console.error(error)
      } finally {
        this.isUpsertLoading = false
      }
    },
    async createArenaVariants(productId, variants) {
      if (!this.arenaId || !productId) {
        return
      }
      for (const variant of variants) {
        if (!variant) continue
        try {
          const { id, prices = [], ...variantPayload } = variant
          if (id) {
            if (Object.keys(variantPayload).length > 0) {
              await ArenaProduct.api.updateArenaVariant(
                this.arenaId,
                productId,
                id,
                variantPayload,
              )
            }
            if (prices.length > 0) {
              await this.updateArenaVariantPrices(productId, id, prices)
            }
          } else {
            await ArenaProduct.api.createArenaVariant(this.arenaId, productId, {
              ...variantPayload,
              prices,
            })
          }
        } catch (error) {
          console.error('Failed to create arena variant', error)
        }
      }
    },
    async deleteArenaVariants(productId, ids) {
      for (const variantId of ids) {
        try {
          await ArenaProduct.api.deleteArenaVariant(
            this.arenaId,
            productId,
            variantId,
          )
        } catch (error) {
          console.error('Failed to delete arena variant', variantId, error)
        }
      }
    },
    async updateArenaVariantPrices(productId, variantId, prices = []) {
      const priceUpdates = []
      prices.forEach((price) => {
        if (!price || !price.gross_price) {
          return
        }
        const grossPrice = Number(price.gross_price)
        if (Number.isNaN(grossPrice)) {
          return
        }
        const payload = {
          gross_price: grossPrice,
          currency_id: price.currency_id,
        }
        if (price.id) {
          priceUpdates.push(
            ArenaProduct.api.updateArenaVariantPrice(
              this.arenaId,
              productId,
              variantId,
              price.id,
              payload,
            ),
          )
        } else {
          priceUpdates.push(
            ArenaProduct.api.createArenaVariantPrice(
              this.arenaId,
              productId,
              variantId,
              payload,
            ),
          )
        }
      })
      if (priceUpdates.length) {
        await Promise.all(priceUpdates)
      }
    },
    async toggleProductTaxes(productPayload) {
      const changes = this.getTaxChanges(productPayload)
      const changeTaxRequests = changes.map(({ action, tax_id }) =>
        ArenaProduct.api[action](this.arenaId, productPayload.id, {
          tax_id,
        }),
      )
      return await Promise.all(changeTaxRequests)
    },
    getTaxChanges(productPayload) {
      const original = (this.productToUpsert?.taxes || []).map((it) => it.id)
      const updated = productPayload.taxes ?? original
      const originalSet = new Set(original)
      const updatedSet = new Set(updated)

      const removed = original
        .filter((tax_id) => !updatedSet.has(tax_id))
        .map((tax_id) => ({ action: 'removeProductTax', tax_id }))

      const added = updated
        .filter((tax_id) => !originalSet.has(tax_id))
        .map((tax_id) => ({ action: 'addProductTax', tax_id }))

      return [...removed, ...added]
    },
    formatSelectedSearchAttributes() {
      const searchValue =
        typeof this.pagination.search === 'string'
          ? this.pagination.search.trim()
          : ''
      if (!searchValue) {
        return ''
      }
      const selected =
        Array.isArray(this.selectedSearchAttributes) &&
        this.selectedSearchAttributes.length
          ? this.selectedSearchAttributes
          : ['Name']
      const searchAttributes = selected.map(
        (attribute) =>
          `${attribute.replace(/ /g, '_').toLowerCase()}=${encodeURIComponent(
            searchValue,
          )}`,
      )
      return `&${searchAttributes.join('&')}`
    },
    handleFiltersChange() {
      if (this.isResettingFilters) {
        return
      }
      if (this.onFirstPage()) {
        this.fetchEntities(this.setParams())
      } else {
        this.setCurrentPageToOne()
      }
    },
    setParams() {
      const perPage = this.pagination.per_page
      const params = {
        page: this.pagination.current_page,
        per_page: perPage,
        size: perPage, // keep backward compatibility with old query param name
      }
      const { sort, has_active_variant, category_code } = this.filters
      if (sort) {
        params.sort = sort
      }
      if (has_active_variant !== null && has_active_variant !== undefined) {
        params.has_active_variant = has_active_variant
      }
      if (category_code) {
        params.category_code = category_code
      }
      if (this.nameFilter?.trim()) {
        params.name = this.nameFilter.trim()
      }
      return params
    },
    setCurrentPageToOne() {
      this.pagination.current_page = 1
    },
    onFirstPage() {
      return this.pagination.current_page === 1
    },
    setPaginationTotals(metadata = null, dataLength = 0) {
      const currentPage = this.pagination.current_page || 1
      const perPage = this.pagination.per_page || 10
      const total = metadata?.count ?? metadata?.total ?? dataLength ?? 0
      const lastPage = perPage ? Math.ceil((total || 0) / perPage) : 0
      const from = total ? (currentPage - 1) * perPage + 1 : 0
      const to = total ? Math.min(from + perPage - 1, total) : 0
      this.pagination.total = total
      this.pagination.last_page = lastPage
      this.pagination.from = from
      this.pagination.to = to
    },
    handleGlobalProductSelected(globalProduct) {
      if (!globalProduct) return
      // Map global product to arena product format
      const arenaProduct = {
        category_id: globalProduct.category_id,
        name: globalProduct.name || '',
        description: globalProduct.description || '',
        image: globalProduct.image || '',
        sort_order: globalProduct.sort_order || 1,
        track_stock: !!globalProduct.track_stock,
        global_product_id: globalProduct.id || '',
        taxes: [], // Will need to be filled by user
        variants: (globalProduct.variants || []).map((variant) => ({
          name: variant.name || '',
          description: variant.description || '',
          sku: variant.sku || '',
          image: variant.image || '',
          attributes: variant.attributes || {},
          pack_type: variant.pack_type || null,
          active: false, // Default to inactive
          sort_order: variant.sort_order || 1,
          global_variant_id: variant.id || '',
          prices: [], // Will need to be filled by user
          stock: {
            available: null,
            waste: null,
          },
        })),
      }
      // Use nextTick to ensure component is fully ready before opening dialog
      this.$nextTick(() => {
        this.openUpsertDialog(arenaProduct)
      })
    },
  },
  watch: {
    arenaId: {
      immediate: true,
      handler(newArenaId) {
        if (newArenaId) {
          this.isResettingFilters = true
          Object.assign(this.pagination, resetPagination())
          Object.assign(this.filters, resetFilters())
          this.nameFilter = ''
          this.isResettingFilters = false
          this.fetchEntities()
        } else {
          this.isResettingFilters = true
          this.products = []
          this.isUpsertDialogOpen = false
          Object.assign(this.pagination, resetPagination())
          Object.assign(this.filters, resetFilters())
          this.nameFilter = ''
          this.isResettingFilters = false
          this.setPaginationTotals()
        }
      },
    },
    selectedGlobalProduct: {
      immediate: true,
      handler(globalProduct) {
        if (globalProduct) {
          this.$nextTick(() => {
            this.handleGlobalProductSelected(globalProduct)
            // Clear the selected product from store
            this.setSelectedGlobalProduct(null)
          })
        }
      },
    },
    isUpsertDialogOpen(value) {
      if (!value) {
        this.productToUpsert = null
      }
    },
    'filters.category_code'() {
      this.handleFiltersChange()
    },
    'filters.has_active_variant'() {
      this.handleFiltersChange()
    },
    'filters.sort'() {
      this.handleFiltersChange()
    },
    'pagination.per_page'(newVal, oldVal) {
      if (this.isResettingFilters || newVal === oldVal) return
      if (this.onFirstPage()) {
        this.fetchEntities(this.setParams())
      } else {
        this.setCurrentPageToOne()
      }
    },
    'pagination.current_page'(newVal, oldVal) {
      if (this.isResettingFilters || newVal === oldVal) return
      this.fetchEntities(this.setParams())
    },
    nameFilter() {
      if (this.nameDebounce) {
        clearTimeout(this.nameDebounce)
      }
      this.nameDebounce = setTimeout(() => {
        if (this.onFirstPage()) {
          this.fetchEntities(this.setParams())
        } else {
          this.setCurrentPageToOne()
        }
      }, 500)
    },
  },
  async created() {
    return await Promise.all([
      this.fetchCategories(),
      this.fetchTaxJurisdiction(),
    ])
  },
  beforeDestroy() {
    if (this.nameDebounce) {
      clearTimeout(this.nameDebounce)
    }
  },
}
</script>
